import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import API_CONFIG from '../../config/api';

const UserCreateModal = ({ open, onClose, onUserCreated, onShowSnackbar }) => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'project_user'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createdUser, setCreatedUser] = useState(null);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('stratovai_access_token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create user');
      }

      const data = await response.json();
      setCreatedUser(data);
      onShowSnackbar('User created successfully!', 'success');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPassword = () => {
    if (createdUser?.temporary_password) {
      navigator.clipboard.writeText(createdUser.temporary_password);
      setPasswordCopied(true);
      onShowSnackbar('Password copied to clipboard!', 'info');
      setTimeout(() => setPasswordCopied(false), 2000);
    }
  };

  const handleFinish = () => {
    // Reset form
    setFormData({
      email: '',
      full_name: '',
      role: 'project_user'
    });
    setCreatedUser(null);
    setPasswordCopied(false);
    setError(null);
    onUserCreated();
  };

  const handleCancel = () => {
    setFormData({
      email: '',
      full_name: '',
      role: 'project_user'
    });
    setCreatedUser(null);
    setPasswordCopied(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      {createdUser ? (
        <>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Create New User</Typography>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, overflow: 'visible' }}>
          {/* Success view - show generated password */}
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              User created successfully! The user must change their password on first login.
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Email
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {createdUser.email}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Full Name
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {createdUser.full_name}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Role
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {createdUser.role?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                ⚠️ Important: Save this password now!
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                This password will only be shown once. The user will be required to change it on first login.
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label="Temporary Password"
              value={createdUser.temporary_password}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyPassword} edge="end">
                      {passwordCopied ? <CheckIcon color="success" /> : <CopyIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { fontFamily: 'monospace', fontSize: '1.1rem' }
              }}
              sx={{ mb: 2 }}
            />

            <Typography variant="caption" color="text.secondary">
              Click the copy icon to copy the password to your clipboard.
            </Typography>
          </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleFinish}
            variant="contained"
            fullWidth
          >
            Done
          </Button>
      </DialogActions>
        </>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', m: 0, p: 0, minWidth: 0 }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Create New User</Typography>
              <IconButton onClick={handleCancel} size="small" type="button">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pt: 2, overflow: 'visible' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              autoFocus
              sx={{ mb: 2 }}
              helperText="User will use this email to log in"
            />

            <TextField
              fullWidth
              label="Full name"
              value={formData.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => handleChange('role', e.target.value)}
              >
                <MenuItem value="project_user">User</MenuItem>
                <MenuItem value="project_manager">Manager</MenuItem>
                <MenuItem value="project_admin">Admin</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
              </Select>
            </FormControl>

            <Alert severity="info">
              A secure password will be automatically generated. The user will be required to change it on first login.
            </Alert>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button type="button" onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !formData.email || !formData.full_name}
              startIcon={loading && <CircularProgress size={16} />}
            >
              {loading ? 'Creating...' : 'Create User'}
            </Button>
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
};

export default UserCreateModal;

